import { useEffect, useRef } from "react";
import * as THREE from "three";
import type { AvatarConfig, ThemeId, TimeOfDay } from "@shared/types";
import { themeFor } from "../game/engine/Scene";
import { SEED_LESSONS } from "../data/seed-lessons";

interface Props {
  avatar: AvatarConfig;
  theme: ThemeId;
  timeOfDay: TimeOfDay;
}

export function MainMenuHero({ avatar, theme, timeOfDay }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const colors = themeFor(theme, timeOfDay);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;

    const scene = new THREE.Scene();
    scene.background = makeLobbyBackdropTexture();
    scene.fog = null;

    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 80);
    camera.position.set(0, 3.7, 9.2);
    camera.lookAt(0, 1.65, 0);

    scene.add(new THREE.HemisphereLight(colors.sky, colors.hemiGround, 1.25));
    const sun = new THREE.DirectionalLight(0xfff1c8, 1.5);
    sun.position.set(-8, 18, 10);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    scene.add(sun);

    const icons = buildWordIconPattern();
    scene.add(icons);
    const rings = buildLobbyRings();
    scene.add(rings);
    const platform = buildLobbyPlatform();
    scene.add(platform);
    const runner = buildMenuRunner(avatar);
    const runnerBaseY = 0.7;
    runner.position.set(0, runnerBaseY, 0.25);
    runner.rotation.y = 0.12;
    runner.scale.setScalar(0.82);
    scene.add(runner);

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    let raf = 0;
    let spinVelocity = 0;
    let leftHeld = false;
    let rightHeld = false;
    let jumpStartedAt = -100;
    const isTypingTarget = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
    };
    const triggerJump = () => {
      jumpStartedAt = (performance.now() - start) * 0.001;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (event.key === "ArrowLeft" || event.key.toLowerCase() === "a") {
        leftHeld = true;
        event.preventDefault();
      } else if (event.key === "ArrowRight" || event.key.toLowerCase() === "d") {
        rightHeld = true;
        event.preventDefault();
      } else if (event.code === "Space") {
        triggerJump();
        event.preventDefault();
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" || event.key.toLowerCase() === "a") leftHeld = false;
      if (event.key === "ArrowRight" || event.key.toLowerCase() === "d") rightHeld = false;
    };
    const onPointerDown = (event: PointerEvent) => {
      triggerJump();
      event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    canvas.addEventListener("pointerdown", onPointerDown);

    const start = performance.now();
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const t = (now - start) * 0.001;
      const spinInput = (rightHeld ? 1 : 0) - (leftHeld ? 1 : 0);
      spinVelocity += spinInput * 0.045;
      spinVelocity *= spinInput ? 0.94 : 0.9;
      spinVelocity = THREE.MathUtils.clamp(spinVelocity, -0.22, 0.22);
      runner.rotation.y += spinVelocity;
      const jumpAge = t - jumpStartedAt;
      const jumpT = jumpAge >= 0 && jumpAge < 0.72 ? jumpAge / 0.72 : 1;
      const jumpLift = jumpT < 1 ? Math.sin(jumpT * Math.PI) * 0.78 : 0;
      runner.position.y = runnerBaseY + jumpLift;
      animateRunner(runner, t, jumpT < 1, jumpT);
      icons.children.forEach((icon, i) => {
        icon.position.x -= 0.012 + (i % 3) * 0.003;
        icon.position.y += Math.sin(t * 1.1 + i) * 0.0015;
        icon.rotation.z = Math.sin(t * 0.9 + i) * 0.08;
        if (icon.position.x < -10) icon.position.x = 10;
      });
      rings.rotation.z = Math.sin(t * 0.18) * 0.025;
      platform.rotation.y = Math.sin(t * 0.7) * 0.05;
      scene.traverse((o) => {
        if (o.userData.spin) o.rotation.y += o.userData.spin;
        if (o.userData.floatBase != null) {
          o.position.y = o.userData.floatBase + Math.sin(t * 2.2 + o.userData.phase) * 0.18;
        }
      });
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      canvas.removeEventListener("pointerdown", onPointerDown);
      renderer.dispose();
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const mat = obj.material;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else mat.dispose();
        }
      });
    };
  }, [avatar, theme, timeOfDay]);

  return <canvas ref={ref} className="home-hero-canvas" aria-hidden="true" />;
}

function buildLevelScene(scene: THREE.Scene, colors: ReturnType<typeof themeFor>) {
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(95, 80),
    new THREE.MeshStandardMaterial({ color: colors.ground, roughness: 0.85 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(0, -0.04, -18);
  ground.receiveShadow = true;
  scene.add(ground);

  const water = new THREE.Mesh(
    new THREE.PlaneGeometry(70, 42),
    new THREE.MeshStandardMaterial({
      color: colors.id === "space" ? 0x161b4a : 0x22b8e8,
      roughness: 0.38,
      transparent: true,
      opacity: colors.id === "space" ? 0.35 : 0.78,
    }),
  );
  water.rotation.x = -Math.PI / 2;
  water.position.set(0, -0.02, -18);
  scene.add(water);

  const pathMat = new THREE.MeshStandardMaterial({ color: colors.pathLight, roughness: 0.78 });
  const pathDark = new THREE.MeshStandardMaterial({ color: colors.pathDark, roughness: 0.78 });
  for (let i = 0; i < 18; i++) {
    const tile = new THREE.Mesh(new THREE.BoxGeometry(4.3 + i * 0.18, 0.18, 1.55), i % 2 ? pathMat : pathDark);
    tile.position.set(0, 0.08, 2.5 - i * 1.55);
    tile.receiveShadow = true;
    scene.add(tile);
  }

  const railMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5 });
  for (const x of [-2.45, 2.45]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.55, 28), railMat);
    rail.position.set(x, 0.45, -10.2);
    rail.castShadow = true;
    scene.add(rail);
  }

  const gate = buildWordGate();
  gate.position.set(0, 0, -9.8);
  gate.scale.setScalar(0.9);
  scene.add(gate);

  for (let i = 0; i < 9; i++) {
    const coin = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.18, 0.05, 18),
      new THREE.MeshStandardMaterial({ color: 0xffd93d, metalness: 0.45, roughness: 0.28 }),
    );
    coin.position.set((i % 3 - 1) * 0.7, 0.58, -2.8 - i * 0.9);
    coin.rotation.z = Math.PI / 2;
    coin.castShadow = true;
    coin.userData.spin = 0.035;
    scene.add(coin);
  }

  const star = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.52, 0),
    new THREE.MeshStandardMaterial({
      color: 0xffd93d,
      emissive: 0xffb020,
      emissiveIntensity: 0.55,
      flatShading: true,
    }),
  );
  star.position.set(0.8, 1.1, -1.6);
  star.userData.floatBase = star.position.y;
  star.userData.phase = 1.3;
  star.userData.spin = 0.018;
  scene.add(star);

  for (let i = 0; i < 18; i++) {
    const side = i % 2 === 0 ? -1 : 1;
    const tree = colors.id === "space" ? buildCrystalTree(i) : buildMenuTree(colors.id);
    tree.position.set(side * (4.3 + (i % 4) * 1.15), 0, 1.6 - i * 1.4);
    tree.scale.setScalar(0.75 + (i % 3) * 0.12);
    scene.add(tree);
  }

  for (let i = 0; i < 7; i++) {
    const mountain = new THREE.Mesh(
      new THREE.ConeGeometry(3.6 + i * 0.25, 7 + i * 0.8, 4),
      new THREE.MeshStandardMaterial({
        color: colors.id === "snow" ? 0xe8f2ff : colors.ground.clone().lerp(new THREE.Color(0xffffff), 0.32),
        flatShading: true,
      }),
    );
    mountain.position.set(-18 + i * 6.5, 3.0, -33 - (i % 2) * 5);
    mountain.rotation.y = Math.PI / 4;
    scene.add(mountain);
  }
}

function buildWordGate() {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color: 0x4caf50,
    emissive: 0x37b73d,
    emissiveIntensity: 0.45,
  });
  for (const x of [-1.85, 1.85]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.28, 2.5, 0.28), mat);
    post.position.set(x, 1.25, 0);
    g.add(post);
  }
  const top = new THREE.Mesh(new THREE.BoxGeometry(4.4, 0.42, 0.32), mat);
  top.position.y = 2.55;
  g.add(top);

  const sign = new THREE.Mesh(
    new THREE.BoxGeometry(2.6, 0.48, 0.08),
    new THREE.MeshStandardMaterial({ color: 0x1f9d55, emissive: 0x1f9d55, emissiveIntensity: 0.4 }),
  );
  sign.position.set(0, 2.6, 0.22);
  g.add(sign);

  const label = new THREE.Mesh(
    new THREE.PlaneGeometry(2.25, 0.38),
    new THREE.MeshBasicMaterial({ map: makeTextTexture("WORD BOOST"), transparent: true }),
  );
  label.position.set(0, 2.62, 0.27);
  g.add(label);
  return g;
}

function buildMenuTree(theme: string) {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.28, 1.8, 7),
    new THREE.MeshStandardMaterial({ color: 0x7a4a28 }),
  );
  trunk.position.y = 0.9;
  trunk.castShadow = true;
  g.add(trunk);
  const leafColor =
    theme === "candy" ? 0xff8bc4 : theme === "snow" ? 0xeaf6ff : theme === "desert" ? 0x2f7d3a : 0x1f8a3b;
  const leaves = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.82, 0),
    new THREE.MeshStandardMaterial({ color: leafColor, flatShading: true }),
  );
  leaves.position.y = 2.1;
  leaves.castShadow = true;
  g.add(leaves);
  return g;
}

function buildCrystalTree(i: number) {
  const g = new THREE.Group();
  const crystal = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.75, 0),
    new THREE.MeshStandardMaterial({ color: i % 2 ? 0x6ec5ff : 0x9b5cf6, emissive: 0x172554, flatShading: true }),
  );
  crystal.position.y = 1.4;
  g.add(crystal);
  return g;
}

function buildLobbyRings() {
  const g = new THREE.Group();
  const ringGeo = new THREE.RingGeometry(1, 1.035, 96);
  for (let i = 0; i < 9; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: i % 2 ? 0xffe35a : 0xfff58d,
      transparent: true,
      opacity: i % 2 ? 0.32 : 0.22,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeo, mat);
    ring.position.set(0, 2.0, -3.8 - i * 0.05);
    ring.scale.setScalar(1.35 + i * 0.72);
    g.add(ring);
  }
  return g;
}

function buildLobbyPlatform() {
  const g = new THREE.Group();
  const top = new THREE.Mesh(
    new THREE.CylinderGeometry(1.92, 2.05, 0.55, 64),
    new THREE.MeshStandardMaterial({
      color: 0xff42d6,
      emissive: 0xff2fb9,
      emissiveIntensity: 0.16,
      roughness: 0.38,
      metalness: 0.12,
    }),
  );
  top.position.y = 0.34;
  top.castShadow = true;
  top.receiveShadow = true;
  g.add(top);
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(1.98, 0.08, 10, 80),
    new THREE.MeshStandardMaterial({ color: 0xff9bf0, roughness: 0.26, metalness: 0.2 }),
  );
  rim.position.y = 0.63;
  rim.rotation.x = Math.PI / 2;
  g.add(rim);
  return g;
}

function buildWordIconPattern() {
  const g = new THREE.Group();
  const urls = SEED_LESSONS.flatMap((lesson) => lesson.words.map((w) => w.imageUrl)).filter(Boolean) as string[];
  const selected = urls.filter((_, i) => i % 3 === 0).slice(0, 18);
  selected.forEach((url, i) => {
    const tex = iconTexture(url);
    const mat = new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(mat);
    const col = i % 6;
    const row = Math.floor(i / 6);
    sprite.position.set(-8 + col * 3.2 + (row % 2) * 1.2, 1.0 + row * 1.1, -4.8 - row * 0.15);
    sprite.scale.setScalar(0.68 + (i % 3) * 0.1);
    g.add(sprite);
  });
  return g;
}

function roundedCapsule(
  radius: number,
  length: number,
  color: THREE.ColorRepresentation,
  axis: "x" | "y" | "z" = "y",
) {
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.58, metalness: 0.04 });
  const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(radius, length, 16, 24), mat);
  if (axis === "x") mesh.rotation.z = Math.PI / 2;
  if (axis === "z") mesh.rotation.x = Math.PI / 2;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function buildMenuRunner(avatar: AvatarConfig) {
  const suit = new THREE.Color(avatar.shirtColor ?? "#ff2ca4");
  const faceColor = new THREE.Color(avatar.skinColor ?? "#ffd6ef").lerp(new THREE.Color("#ffffff"), 0.34);
  const g = new THREE.Group();
  const body = new THREE.Group();
  g.add(body);

  const torso = new THREE.Mesh(
    new THREE.SphereGeometry(0.92, 32, 32),
    new THREE.MeshStandardMaterial({ color: suit, roughness: 0.55, metalness: 0.03 }),
  );
  torso.scale.set(0.9, 1.34, 0.68);
  torso.position.y = 1.62;
  torso.castShadow = true;
  body.add(torso);

  const face = new THREE.Mesh(
    new THREE.SphereGeometry(0.38, 24, 20),
    new THREE.MeshStandardMaterial({ color: faceColor, roughness: 0.5 }),
  );
  face.scale.set(1.05, 1.18, 0.15);
  face.position.set(0, 2.05, 0.63);
  body.add(face);
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x111111 });
  for (const dx of [-0.15, 0.15]) {
    const eye = new THREE.Mesh(new THREE.CapsuleGeometry(0.035, 0.12, 6, 12), eyeMat);
    eye.rotation.z = Math.PI;
    eye.position.set(dx, 2.07, 0.7);
    body.add(eye);
  }

  const arm = (x: number) => {
    const pivot = new THREE.Group();
    pivot.position.set(x, 1.75, 0.02);
    const mesh = roundedCapsule(0.16, 0.82, suit);
    mesh.position.y = -0.48;
    mesh.rotation.z = x < 0 ? 0.18 : -0.18;
    pivot.add(mesh);
    body.add(pivot);
    return pivot;
  };
  const leg = (x: number) => {
    const pivot = new THREE.Group();
    pivot.position.set(x, 0.58, 0.1);
    const mesh = roundedCapsule(0.18, 0.46, suit);
    mesh.position.y = -0.3;
    pivot.add(mesh);
    body.add(pivot);
    return pivot;
  };

  const la = arm(-0.64);
  const ra = arm(0.64);
  const ll = leg(-0.28);
  const rl = leg(0.28);
  g.userData.parts = { body, la, ra, ll, rl };
  return g;
}

function animateRunner(runner: THREE.Object3D, t: number, jumping = false, jumpT = 1) {
  const parts = runner.userData.parts as
    | { body: THREE.Object3D; la: THREE.Object3D; ra: THREE.Object3D; ll: THREE.Object3D; rl: THREE.Object3D }
    | undefined;
  if (!parts) return;
  const bounce = Math.abs(Math.sin(t * 3.2)) * 0.16;
  const jumpArc = jumping ? Math.sin(jumpT * Math.PI) : 0;
  const breath = Math.sin(t * 2.1) * 0.035;
  parts.body.position.y = jumping ? bounce * 0.24 : breath * 0.35;
  parts.body.rotation.x = jumping ? -0.1 - jumpArc * 0.18 : 0;
  parts.body.rotation.z = jumping ? Math.sin(t * 2.2) * 0.04 : 0;
  parts.body.rotation.y = 0;
  parts.body.scale.set(1 - breath * 0.12, 1 + breath, 1 - breath * 0.12);
  if (jumping) {
    parts.la.rotation.x = -2.25 + jumpArc * 0.18;
    parts.ra.rotation.x = -2.25 + jumpArc * 0.18;
    parts.la.rotation.z = 0.45 + jumpArc * 0.18;
    parts.ra.rotation.z = -0.45 - jumpArc * 0.18;
    parts.ll.rotation.x = -0.95 + jumpArc * 0.35;
    parts.rl.rotation.x = -0.95 + jumpArc * 0.35;
  } else {
    parts.la.rotation.x = 0.08 + breath * 0.35;
    parts.ra.rotation.x = 0.08 + breath * 0.35;
    parts.la.rotation.z = 0.28 + breath * 0.18;
    parts.ra.rotation.z = -0.28 - breath * 0.18;
    parts.ll.rotation.x = 0;
    parts.rl.rotation.x = 0;
  }
}

const skyCache = new Map<string, THREE.CanvasTexture>();
const iconTextureCache = new Map<string, THREE.Texture>();

function iconTexture(url: string) {
  const cached = iconTextureCache.get(url);
  if (cached) return cached;
  const tex = new THREE.TextureLoader().load(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  iconTextureCache.set(url, tex);
  return tex;
}

let lobbyBackdrop: THREE.CanvasTexture | null = null;
function makeLobbyBackdropTexture() {
  if (lobbyBackdrop) return lobbyBackdrop;
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createRadialGradient(512, 450, 80, 512, 512, 760);
  grad.addColorStop(0, "#ffe96b");
  grad.addColorStop(0.42, "#ffc914");
  grad.addColorStop(1, "#ffb000");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1024, 1024);
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 30;
  for (let r = 120; r < 900; r += 90) {
    ctx.beginPath();
    ctx.arc(512, 460, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  lobbyBackdrop = new THREE.CanvasTexture(canvas);
  lobbyBackdrop.colorSpace = THREE.SRGBColorSpace;
  return lobbyBackdrop;
}

function makeSkyTexture(top: THREE.Color, bottom: THREE.Color) {
  const key = `${top.getHexString()}-${bottom.getHexString()}`;
  const cached = skyCache.get(key);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = 4;
  canvas.height = 512;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createLinearGradient(0, 0, 0, 512);
  grad.addColorStop(0, `#${top.getHexString()}`);
  grad.addColorStop(1, `#${bottom.getHexString()}`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 4, 512);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  skyCache.set(key, tex);
  return tex;
}

const textCache = new Map<string, THREE.CanvasTexture>();
function makeTextTexture(text: string) {
  const cached = textCache.get(text);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = "900 58px Nunito, Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = 10;
  ctx.strokeStyle = "rgba(0, 95, 44, 0.75)";
  ctx.strokeText(text, 256, 66);
  ctx.fillStyle = "#ffffff";
  ctx.fillText(text, 256, 66);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  textCache.set(text, tex);
  return tex;
}
